"use client";

import Link from "next/link";
import { useState } from "react";
import { CheckIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import OtpInput from "react-otp-input";

import { errorIntoString } from "@/lib/utils";
import {
	useCompleteForgotPasswordMutation,
	useForgotPasswordMutation,
	useVerifyForgotPasswordOtpMutation,
} from "@/features/auth/authApiInjection";
import {
	emailPasswordResetSchema,
	EmailPasswordSchema,
	forgotPasswordCompleteSchema,
	ForgotPasswordCompleteSchema,
} from "@/lib/schemas/email-password-reset";
import { useSafeReCaptcha } from "@/hooks/useSafeReCaptcha";

import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TextInput from "@/components/form/TextInput";
import PasswordInput from "@/components/form/PasswordInput";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";

type Step = "email" | "otp" | "password" | "success";

export default function Page() {
	const { toast } = useToast();
	const { executeRecaptcha } = useSafeReCaptcha();
	const [step, setStep] = useState<Step>("email");
	const [email, setEmail] = useState("");
	const [otp, setOtp] = useState("");
	const [stepToken, setStepToken] = useState("");
	const [otpFieldKey, setOtpFieldKey] = useState(0);

	const emailForm = useForm<EmailPasswordSchema>({
		resolver: zodResolver(emailPasswordResetSchema),
		defaultValues: { email: "" },
	});

	const passwordForm = useForm<ForgotPasswordCompleteSchema>({
		resolver: zodResolver(forgotPasswordCompleteSchema),
		defaultValues: { newPassword: "", confirmPassword: "" },
	});

	const [requestCode, { isLoading: isRequesting }] = useForgotPasswordMutation();
	const [verifyOtp, { isLoading: isVerifying }] =
		useVerifyForgotPasswordOtpMutation();
	const [completeReset, { isLoading: isCompleting }] =
		useCompleteForgotPasswordMutation();

	const cardClass =
		"w-full max-w-md rounded-3xl border-none px-1 !shadow-custom md:px-6";

	const requestCaptcha = async () => {
		if (!executeRecaptcha) return undefined;
		try {
			return await executeRecaptcha("forgot_password");
		} catch (error) {
			console.error("reCAPTCHA error:", error);
			if (process.env.NODE_ENV === "production") {
				toast({
					title: "Security verification failed",
					description: "Please try again.",
					variant: "destructive",
				});
			}
			return undefined;
		}
	};

	const onEmailSubmit = async (values: EmailPasswordSchema) => {
		const captchaToken = await requestCaptcha();
		if (process.env.NODE_ENV === "production" && executeRecaptcha && !captchaToken) {
			return;
		}

		const resp = await requestCode({
			email: values.email,
			captchaToken,
		});

		if ("error" in resp) {
			toast({
				variant: "destructive",
				title: "Failed to send verification code",
				description: errorIntoString(resp.error ?? "Unknown Error"),
			});
			return;
		}

		setEmail(values.email.trim().toLowerCase());
		setOtp("");
		setOtpFieldKey((key) => key + 1);
		setStep("otp");
	};

	const onVerifyOtp = async () => {
		if (otp.length !== 4) return;

		const resp = await verifyOtp({
			email,
			code: otp.trim().padStart(4, "0"),
		});

		if ("error" in resp) {
			toast({
				variant: "destructive",
				title: "Verification failed",
				description: errorIntoString(resp.error ?? "Invalid code"),
			});
			setOtp("");
			setOtpFieldKey((key) => key + 1);
			return;
		}

		setStepToken(resp.data.stepToken);
		setStep("password");
	};

	const onPasswordSubmit = async (values: ForgotPasswordCompleteSchema) => {
		const resp = await completeReset({
			email,
			stepToken,
			newPassword: values.newPassword,
			confirmPassword: values.confirmPassword,
		});

		if ("error" in resp) {
			toast({
				variant: "destructive",
				title: "Failed to reset password",
				description: errorIntoString(resp.error ?? "Unknown Error"),
			});
			return;
		}

		setStep("success");
	};

	if (step === "success") {
		return (
			<Card className={cardClass}>
				<CardHeader className="px-8 pt-14">
					<CheckIcon strokeWidth={4} className="mx-auto mb-4 size-20 text-tertiary" />
					<CardTitle className="text-xl font-semibold xs:text-2xl sm:text-2xl md:text-3xl">
						Password updated{" "}
						<span className="text-accent-foreground">successfully</span>
					</CardTitle>
					<CardDescription>
						You can now log in with your new password.
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col px-8 pb-8">
					<Button asChild className="mx-4 h-12 shadow-custom sm:mx-6">
						<Link href="/login">Go to login</Link>
					</Button>
				</CardContent>
			</Card>
		);
	}

	if (step === "password") {
		return (
			<Card className={cardClass}>
				<CardHeader className="px-8 pt-14">
					<p className="text-xs font-semibold text-primary">Step 2 of 2</p>
					<CardTitle className="text-xl font-semibold xs:text-2xl sm:text-2xl md:text-3xl">
						Set a new <span className="text-accent-foreground">password</span>
					</CardTitle>
					<CardDescription>
						Choose a new password for {email}.
					</CardDescription>
				</CardHeader>
				<CardContent className="px-8">
					<Form {...passwordForm}>
						<form
							onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
							className="flex flex-col justify-center"
						>
							<PasswordInput
								name="newPassword"
								label="New Password"
								placeholder="Enter new password"
								autoComplete="new-password"
							/>
							<PasswordInput
								name="confirmPassword"
								label="Confirm Password"
								placeholder="Confirm new password"
								autoComplete="new-password"
							/>
							<Button
								type="submit"
								disabled={isCompleting}
								className="mx-4 mt-6 h-12 shadow-custom sm:mx-6"
							>
								{isCompleting ? <LoadingSpinner layout="inline" fullScreen={false} /> : "Reset password"}
							</Button>
						</form>
					</Form>
				</CardContent>
			</Card>
		);
	}

	if (step === "otp") {
		return (
			<Card className={cardClass}>
				<CardHeader className="px-8 pt-14">
					<p className="text-xs font-semibold text-primary">Step 1 of 2</p>
					<CardTitle className="text-xl font-semibold xs:text-2xl sm:text-2xl md:text-3xl">
						Verify your <span className="text-accent-foreground">email</span>
					</CardTitle>
					<CardDescription>
						Enter the verification code sent to {email}.
					</CardDescription>
				</CardHeader>
				<CardContent className="px-8">
					<div
						key={`forgot-password-otp-${otpFieldKey}`}
						className="mb-6 grid grid-cols-4 justify-items-center gap-4"
					>
						<OtpInput
							value={otp}
							onChange={setOtp}
							numInputs={4}
							containerStyle={{ display: "contents" }}
							renderInput={(props, index) => (
								<Input
									{...props}
									aria-label={`Verification code digit ${index + 1}`}
									className="h-12 w-11 border border-accent-foreground/50 text-center"
								/>
							)}
							inputStyle={{ width: 48 }}
							inputType="number"
						/>
					</div>
					<Button
						type="button"
						disabled={otp.length !== 4 || isVerifying}
						onClick={() => void onVerifyOtp()}
						className="mx-4 h-12 w-[calc(100%-2rem)] shadow-custom sm:mx-6 sm:w-[calc(100%-3rem)]"
					>
						{isVerifying ? <LoadingSpinner layout="inline" fullScreen={false} /> : "Verify code"}
					</Button>
					<Button
						type="button"
						variant="ghost"
						disabled={isRequesting}
						onClick={async () => {
							const resp = await requestCode({ email });
							if (!("error" in resp)) {
								toast({
									title: "Code resent",
									description: `A new code was sent to ${email}.`,
								});
								setOtp("");
								setOtpFieldKey((key) => key + 1);
							}
						}}
						className="mx-4 mt-2 w-[calc(100%-2rem)] sm:mx-6 sm:w-[calc(100%-3rem)]"
					>
						Resend code
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className={cardClass}>
			<CardHeader className="px-8 pt-14">
				<CardTitle className="text-xl font-semibold xs:text-2xl sm:text-2xl md:text-3xl">
					Reset your <span className="text-accent-foreground">password</span>
				</CardTitle>
				<CardDescription>
					Enter the email on your account. We will send a verification code to
					continue.
				</CardDescription>
			</CardHeader>

			<CardContent className="px-8">
				<Form {...emailForm}>
					<form
						onSubmit={emailForm.handleSubmit(onEmailSubmit)}
						className="flex flex-col justify-center"
					>
						<TextInput name="email" label="Email" placeholder="Email" />
						<Button
							type="submit"
							disabled={isRequesting}
							className="mx-4 mt-6 h-12 shadow-custom sm:mx-6"
						>
							{isRequesting ? <LoadingSpinner layout="inline" fullScreen={false} /> : "Continue"}
						</Button>
					</form>
				</Form>
			</CardContent>

			<CardFooter className="flex justify-center gap-1 py-8 text-center text-xs text-muted-foreground">
				<span>Remember your password? </span>
				<Link className="text-accent-foreground underline" href="/login">
					Log in
				</Link>
			</CardFooter>
		</Card>
	);
}
